import { ReactElement, useEffect, useRef, useState } from "react";
import { useDependency } from "../../../contexts/DependencyContext";
import { useEventListener } from "../../../hooks/UseEventListener";
import { CalendarDate } from "../../../services/datepicker/interfaces/calendar-date";
import DatePickerService from "../../../services/datepicker/datepicker-service";
import { addZero, className } from "../../../shared/helpers";
import Calendar from "./Calendar";
import TimeSelector from "./TimeSelector";
import styles from "./Datepicker.module.scss";

interface DatePickerProps
{
    title?: string;
    locale?: string;
    currentDate?: Date;
    customClassName?: string;
    pickDate: (date: CalendarDate) => void;
}

const Datepicker = (props: DatePickerProps): ReactElement =>
{
    const datepickerService = useDependency(DatePickerService);
    const datepickerRef = useRef<HTMLDivElement>(null as any);
    const initialDate = useRef<CalendarDate>(null as any);
    const [currentDate, setCurrentDate] = useState<CalendarDate>(datepickerService.currentDate);
    const [selectedDate, setSelectedDate] = useState<CalendarDate>(null as any);
    const [datePickerVisible, setDatePickerVisible] = useState<boolean>(false);


    function handleClickOutside(e: Event): void
    {
        if (datepickerRef.current && !datepickerRef.current.contains(e.target as HTMLElement)) {
            setDatePickerVisible(false);
            if (initialDate.current && selectedDate && initialDate.current !== selectedDate) {
                const initDate = Object.values(initialDate.current).join();
                const selectDate = Object.values(selectedDate).join();
                if (initDate !== selectDate) confirm();
            };
        }
    }

    function setTogglerText(): string
    {
        const date = selectedDate ?? currentDate;
        const month = addZero(selectedDate ? (selectedDate.month + 1) : (currentDate.month + 1));
        return `${month}/${addZero(date?.day)}/${date?.year}`
    }

    function setTogglerTime(): string | null
    {
        const date = selectedDate ?? currentDate;
        if (date?.hours === undefined && date?.minutes === undefined) return null;
        return `${addZero(date?.hours ?? 0)}:${addZero(date?.minutes ?? 0)}`;
    }

    function cancel(): void
    {
        setSelectedDate(null as any);
        setDatePickerVisible(false);
        setSelectedDate(initialDate.current);
    }

    function confirm(): void
    {
        const min = parseInt(process.env.REACT_APP_DATEPICKER_MIN! ?? 1950);

        if (selectedDate && datePickerVisible) 
        {
            if(selectedDate.year < min)
            {
                selectedDate.year = 2000;
            }
            props.pickDate(selectedDate);
        }
        
        setDatePickerVisible(false);
    }

    useEffect(() =>
    {
        if (!props.currentDate) return;

        const currentDate = datepickerService.setCurrentDate({
            day: props.currentDate.getDate(),
            month: props.currentDate.getMonth(),
            year: props.currentDate.getFullYear(),
            hours: props.currentDate.getHours(),
            minutes: props.currentDate.getMinutes(),
            seconds: props.currentDate.getSeconds()
        })

        initialDate.current = currentDate
        setCurrentDate(currentDate);
        setSelectedDate(currentDate);
    }, [datepickerService, props.currentDate, props.pickDate]);

    useEffect(() =>
    {
        if (!selectedDate) return;
        setCurrentDate(datepickerService.setCurrentDate(selectedDate));
    }, [datepickerService, selectedDate]);

    useEventListener("click", handleClickOutside);

    return (
        <div
            ref={datepickerRef}
            {...className(
                styles.wrapper,
                props.customClassName,
                { [styles.visible]: datePickerVisible })
            }
        >
            <button
                className={styles.toggler}
                onClick={() => setDatePickerVisible(!datePickerVisible)}
            >
                <span>{setTogglerText()}</span>
                <span className={styles.time}>{setTogglerTime()}</span>
                <i className="material-icons">expand_more</i>
            </button>

            {datePickerVisible && (
                <Calendar
                    title={props.title}
                    currentDate={currentDate}
                    selectedDate={selectedDate}
                    datepickerService={datepickerService}
                    updateDate={(date: CalendarDate) => setCurrentDate(date)}
                    selectDate={(date: CalendarDate) => setSelectedDate(date)}
                    close={() => setDatePickerVisible(false)}
                >
                    <TimeSelector
                        currentDate={currentDate}
                        selectedDate={selectedDate}
                        datepickerService={datepickerService}
                        updateDate={(date: CalendarDate) => setSelectedDate(date)}
                    />

                    <div className={styles.actions}>
                        <button {...className(styles.btn, styles.cancel)} onClick={cancel}>Cancel</button>
                        <button {...className(styles.btn, styles.confirm)} onClick={confirm} >Ok</button>
                    </div>
                </Calendar>
            )}
        </div>
    );
};

export default Datepicker;
